import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useState, useEffect, useRef, type ReactNode } from 'react'
import { Save, RotateCcw, UserX, UserMinus, Package, Copy, ShieldOff, Filter, Plus, Trash2, X, ChevronDown, Mail, Cloud, FileText, MessageSquare, Sparkles } from 'lucide-react'

interface RulesConfig {
  tenant_id: string
  rule_inactive_enabled: number
  rule_disabled_enabled: number
  rule_downgrade_enabled: number
  rule_excess_enabled: number
  rule_redundant_enabled: number
  inactive_days_threshold: number
  inactive_high_severity_days: number
  disabled_days_threshold: number
  downgrade_max_calls: number
  downgrade_max_meetings: number
  downgrade_active_days: number
  excess_pct_threshold: number
  excess_min_units: number
  excess_reducible_pct: number
  rule_exchange_lowuse_enabled: number
  exchange_min_sent: number
  exchange_min_received: number
  exchange_min_read: number
  rule_onedrive_lowuse_enabled: number
  onedrive_min_viewed: number
  onedrive_min_synced: number
  onedrive_min_shared: number
  rule_sharepoint_lowuse_enabled: number
  sharepoint_min_viewed: number
  sharepoint_min_synced: number
  sharepoint_min_pages: number
  rule_teams_lowuse_enabled: number
  teams_min_chat: number
  teams_min_calls: number
  teams_min_meetings: number
  rule_copilot_nouse_enabled: number
  copilot_inactive_days: number
  dismiss_cooldown_days: number
  expiration_days: number
}

const DEFAULTS: Omit<RulesConfig, 'tenant_id'> = {
  rule_inactive_enabled: 1,
  rule_disabled_enabled: 1,
  // Optimización avanzada: OFF por defecto (el admin la activa tras revisarla).
  rule_downgrade_enabled: 0,
  rule_excess_enabled: 1,
  rule_redundant_enabled: 1,
  inactive_days_threshold: 90,
  inactive_high_severity_days: 180,
  disabled_days_threshold: 0,
  downgrade_max_calls: 0,
  downgrade_max_meetings: 3,
  downgrade_active_days: 90,
  excess_pct_threshold: 20,
  excess_min_units: 5,
  excess_reducible_pct: 50,
  // Infrautilización por servicio (alertas) — OFF por defecto.
  rule_exchange_lowuse_enabled: 0,
  exchange_min_sent: 5,
  exchange_min_received: 20,
  exchange_min_read: 20,
  rule_onedrive_lowuse_enabled: 0,
  onedrive_min_viewed: 5,
  onedrive_min_synced: 1,
  onedrive_min_shared: 1,
  rule_sharepoint_lowuse_enabled: 0,
  sharepoint_min_viewed: 5,
  sharepoint_min_synced: 1,
  sharepoint_min_pages: 5,
  rule_teams_lowuse_enabled: 0,
  teams_min_chat: 10,
  teams_min_calls: 1,
  teams_min_meetings: 3,
  // Copilot sin uso — OFF por defecto. 90 días = 3 meses sin actividad.
  rule_copilot_nouse_enabled: 0,
  copilot_inactive_days: 90,
  dismiss_cooldown_days: 90,
  expiration_days: 365,
}

interface RecException {
  id: string
  kind: 'license' | 'user_filter'
  sku_part_number: string | null
  license_label: string | null
  filter_json: string | null
  reason: string | null
  created_at: string
}

interface LicenseRow {
  id: string
  sku_part_number: string
  display_name: string
  custom_display_name: string | null
}

interface UserFilterForm {
  name: string
  email: string
  dept: string
  accountEnabled: string
}

const EMPTY_USER_FILTER: UserFilterForm = { name: '', email: '', dept: '', accountEnabled: '' }

function describeUserFilter(filterJson: string | null): string {
  if (!filterJson) return '—'
  try {
    const f = JSON.parse(filterJson) as Partial<UserFilterForm>
    const parts: string[] = []
    if (f.name) parts.push(`Nombre: "${f.name}"`)
    if (f.email) parts.push(`Email: "${f.email}"`)
    if (f.dept) parts.push(`Departamento: "${f.dept}"`)
    if (f.accountEnabled === 'true') parts.push('Estado: Activos')
    if (f.accountEnabled === 'false') parts.push('Estado: Deshabilitados')
    return parts.join(' · ') || '—'
  } catch {
    return '—'
  }
}

/**
 * Combobox de solo-seleccion: muestra los valores reales de /users y permite
 * escribir para filtrar la lista, pero el valor final siempre sale de ella.
 */
function SearchableSelect({
  label, value, onChange, options, placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  const filtered = options
    .filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 50)

  const select = (v: string) => {
    onChange(v)
    setOpen(false)
    setSearch('')
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
        {label}
      </label>
      {value ? (
        <div
          className="flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <span className="truncate" title={value} style={{ color: 'var(--color-text-primary)' }}>{value}</span>
          <button
            type="button"
            onClick={() => onChange('')}
            className="shrink-0 rounded p-0.5 hover:bg-gray-100"
            title="Quitar"
          >
            <X className="h-3.5 w-3.5" style={{ color: 'var(--color-text-tertiary)' }} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full rounded-md border px-3 py-2 pr-8 text-sm"
            style={{ borderColor: 'var(--color-border)' }}
          />
          <ChevronDown
            className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2"
            style={{ color: 'var(--color-text-tertiary)' }}
          />
        </div>
      )}
      {open && !value && (
        <ul
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border shadow-lg"
          style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
              Sin resultados
            </li>
          ) : (
            filtered.map((o) => (
              <li key={o}>
                <button
                  type="button"
                  onClick={() => select(o)}
                  className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-gray-100"
                  style={{ color: 'var(--color-text-primary)' }}
                  title={o}
                >
                  {o}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

function ExceptionsSection() {
  const queryClient = useQueryClient()

  const { data: exceptions } = useQuery({
    queryKey: ['settings', 'recommendation-exceptions'],
    queryFn: () => api.get<RecException[]>('/recommendation-rules/exceptions'),
  })

  const { data: licenses } = useQuery({
    queryKey: ['licenses'],
    queryFn: () => api.get<LicenseRow[]>('/licenses'),
  })

  const { data: userOptions } = useQuery({
    queryKey: ['settings', 'recommendation-exceptions', 'user-options'],
    queryFn: () =>
      api.get<{ names: string[]; emails: string[]; departments: string[] }>(
        '/recommendation-rules/exceptions/user-options',
      ),
    staleTime: 5 * 60 * 1000,
  })

  const [selectedSku, setSelectedSku] = useState('')
  const [userFilter, setUserFilter] = useState<UserFilterForm>(EMPTY_USER_FILTER)

  const licenseExceptions = (exceptions ?? []).filter((e) => e.kind === 'license')
  const userFilterExceptions = (exceptions ?? []).filter((e) => e.kind === 'user_filter')

  // Unique SKUs of the tenant not yet excluded
  const excludedSkus = new Set(licenseExceptions.map((e) => e.sku_part_number))
  const availableLicenses = Array.from(
    new Map(
      (licenses ?? [])
        .filter((l) => !excludedSkus.has(l.sku_part_number))
        .map((l) => [l.sku_part_number, l]),
    ).values(),
  ).sort((a, b) =>
    (a.custom_display_name ?? a.display_name).localeCompare(b.custom_display_name ?? b.display_name),
  )

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/recommendation-rules/exceptions', body),
    onSuccess: () => {
      toast.success('Excepcion creada')
      queryClient.invalidateQueries({ queryKey: ['settings', 'recommendation-exceptions'] })
      setSelectedSku('')
      setUserFilter(EMPTY_USER_FILTER)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/recommendation-rules/exceptions/${id}`),
    onSuccess: () => {
      toast.success('Excepcion eliminada')
      queryClient.invalidateQueries({ queryKey: ['settings', 'recommendation-exceptions'] })
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const addLicenseException = () => {
    const lic = availableLicenses.find((l) => l.sku_part_number === selectedSku)
    if (!lic) return
    createMutation.mutate({
      kind: 'license',
      skuPartNumber: lic.sku_part_number,
      licenseLabel: lic.custom_display_name ?? lic.display_name,
    })
  }

  const addUserFilterException = () => {
    const filter: Record<string, string> = {}
    if (userFilter.name.trim()) filter.name = userFilter.name.trim()
    if (userFilter.email.trim()) filter.email = userFilter.email.trim()
    if (userFilter.dept.trim()) filter.dept = userFilter.dept.trim()
    if (userFilter.accountEnabled) filter.accountEnabled = userFilter.accountEnabled
    if (Object.keys(filter).length === 0) {
      toast.error('Indica al menos un criterio para el filtro')
      return
    }
    createMutation.mutate({ kind: 'user_filter', filter })
  }

  const inputStyle = { borderColor: 'var(--color-border)' }

  return (
    <>
      {/* Exception: by license type */}
      <div className="rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ backgroundColor: 'var(--color-gray-100)' }}>
            <ShieldOff className="h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Excepciones por tipo de licencia
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              No se generaran recomendaciones que afecten a las licencias indicadas (p. ej. Microsoft Teams Rooms Pro)
            </p>
          </div>
        </div>

        <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-64">
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Licencia
              </label>
              <select
                value={selectedSku}
                onChange={(e) => setSelectedSku(e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle}
              >
                <option value="">Selecciona una licencia...</option>
                {availableLicenses.map((l) => (
                  <option key={l.sku_part_number} value={l.sku_part_number}>
                    {l.custom_display_name ?? l.display_name}
                  </option>
                ))}
              </select>
            </div>
            <Button size="sm" onClick={addLicenseException} disabled={!selectedSku} loading={createMutation.isPending}>
              <Plus className="h-3.5 w-3.5" /> Anadir excepcion
            </Button>
          </div>

          {licenseExceptions.length > 0 ? (
            <ul className="mt-4 space-y-2">
              {licenseExceptions.map((e) => (
                <li key={e.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--color-border)' }}>
                  <div>
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {e.license_label ?? e.sku_part_number}
                    </span>
                    <span className="ml-2 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                      {e.sku_part_number}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(e.id)}
                    className="rounded p-1 hover:bg-gray-100"
                    title="Eliminar excepcion"
                  >
                    <Trash2 className="h-4 w-4" style={{ color: 'var(--color-error-dark, #b91c1c)' }} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              No hay excepciones por licencia configuradas.
            </p>
          )}
        </div>
      </div>

      {/* Exception: by users filter */}
      <div className="rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ backgroundColor: 'var(--color-gray-100)' }}>
            <Filter className="h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Excepciones por filtro de usuarios
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              No se generaran recomendaciones para los usuarios que cumplan el filtro (mismos criterios que en la pagina de Usuarios)
            </p>
          </div>
        </div>

        <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <SearchableSelect
              label="Nombre"
              value={userFilter.name}
              onChange={(v) => setUserFilter((f) => ({ ...f, name: v }))}
              options={userOptions?.names ?? []}
              placeholder="Buscar nombre..."
            />
            <SearchableSelect
              label="Email"
              value={userFilter.email}
              onChange={(v) => setUserFilter((f) => ({ ...f, email: v }))}
              options={userOptions?.emails ?? []}
              placeholder="Buscar email..."
            />
            <SearchableSelect
              label="Departamento"
              value={userFilter.dept}
              onChange={(v) => setUserFilter((f) => ({ ...f, dept: v }))}
              options={userOptions?.departments ?? []}
              placeholder="Buscar departamento..."
            />
            <div>
              <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                Estado
              </label>
              <select
                value={userFilter.accountEnabled}
                onChange={(e) => setUserFilter((f) => ({ ...f, accountEnabled: e.target.value }))}
                className="w-full rounded-md border px-3 py-2 text-sm"
                style={inputStyle}
              >
                <option value="">Cualquiera</option>
                <option value="true">Activos</option>
                <option value="false">Deshabilitados</option>
              </select>
            </div>
          </div>
          <div className="mt-3">
            <Button size="sm" onClick={addUserFilterException} loading={createMutation.isPending}>
              <Plus className="h-3.5 w-3.5" /> Anadir excepcion
            </Button>
          </div>

          {userFilterExceptions.length > 0 ? (
            <ul className="mt-4 space-y-2">
              {userFilterExceptions.map((e) => (
                <li key={e.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  style={{ borderColor: 'var(--color-border)' }}>
                  <span style={{ color: 'var(--color-text-primary)' }}>{describeUserFilter(e.filter_json)}</span>
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(e.id)}
                    className="rounded p-1 hover:bg-gray-100"
                    title="Eliminar excepcion"
                  >
                    <Trash2 className="h-4 w-4" style={{ color: 'var(--color-error-dark, #b91c1c)' }} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              No hay excepciones por filtro de usuarios configuradas.
            </p>
          )}
        </div>
      </div>
    </>
  )
}

function Toggle({ enabled, onChange, disabled }: { enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
      style={{
        backgroundColor: enabled ? 'var(--color-brand-secondary)' : 'var(--color-gray-300)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="pointer-events-none inline-block h-5 w-5 rounded-full shadow ring-0 transition-transform duration-200"
        style={{
          backgroundColor: 'white',
          transform: enabled ? 'translateX(1.25rem)' : 'translateX(0)',
        }}
      />
    </button>
  )
}

function NumberField({
  label, value, onChange, suffix, min, max, help,
}: {
  label: string; value: number; onChange: (v: number) => void
  suffix?: string; min?: number; max?: number; help?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          min={min ?? 0}
          max={max}
          className="w-24 rounded-md border px-3 py-2 text-sm tabular-nums"
          style={{ borderColor: 'var(--color-border)' }}
        />
        {suffix && <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{suffix}</span>}
      </div>
      {help && <p className="mt-1 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{help}</p>}
    </div>
  )
}

/** Tarjeta de una regla: cabecera con icono + toggle, y panel de ajustes plegable. */
function RuleCard({
  icon: Icon,
  iconBg,
  iconColor,
  title,
  description,
  enabled,
  onToggle,
  children,
}: {
  icon: typeof UserX
  iconBg: string
  iconColor: string
  title: string
  description: string
  enabled: boolean
  onToggle: (v: boolean) => void
  children?: ReactNode
}) {
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: iconBg }}>
            <Icon className="h-5 w-5" style={{ color: iconColor }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{title}</h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{description}</p>
          </div>
        </div>
        <Toggle enabled={enabled} onChange={onToggle} />
      </div>
      {enabled && children}
    </div>
  )
}

export function SettingsRulesPage() {
  const queryClient = useQueryClient()

  const { data: config, isLoading } = useQuery({
    queryKey: ['settings', 'recommendation-rules'],
    queryFn: () => api.get<RulesConfig>('/recommendation-rules'),
  })

  const [form, setForm] = useState<Omit<RulesConfig, 'tenant_id'>>(DEFAULTS)
  const [dirty, setDirty] = useState(false)
  const [activeTab, setActiveTab] = useState<'cleanup' | 'advanced' | 'exceptions'>('cleanup')

  useEffect(() => {
    if (config) {
      const { tenant_id: _, ...rest } = config as RulesConfig & Record<string, unknown>
      setForm({ ...DEFAULTS, ...rest })
      setDirty(false)
    }
  }, [config])

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setDirty(true)
  }

  const saveMutation = useMutation({
    mutationFn: () => api.put('/recommendation-rules', form),
    onSuccess: () => {
      toast.success('Configuracion de reglas guardada')
      queryClient.invalidateQueries({ queryKey: ['settings', 'recommendation-rules'] })
      setDirty(false)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const resetToDefaults = () => {
    setForm(DEFAULTS)
    setDirty(true)
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>Reglas de recomendaciones</CardTitle></CardHeader>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-100)' }} />
          ))}
        </div>
      </Card>
    )
  }

  const cleanupActive =
    form.rule_inactive_enabled + form.rule_disabled_enabled + form.rule_excess_enabled + form.rule_redundant_enabled
  const advancedActive =
    form.rule_exchange_lowuse_enabled +
    form.rule_onedrive_lowuse_enabled +
    form.rule_sharepoint_lowuse_enabled +
    form.rule_teams_lowuse_enabled +
    form.rule_copilot_nouse_enabled

  const TABS = [
    { key: 'cleanup' as const, label: 'Limpieza de licencias', badge: `${cleanupActive}/4` },
    { key: 'advanced' as const, label: 'Optimización avanzada', badge: `${advancedActive}/5` },
    { key: 'exceptions' as const, label: 'Excepciones y ciclo de vida', badge: null },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reglas de recomendaciones</CardTitle>
        <Button variant="secondary" size="sm" onClick={resetToDefaults}>
          <RotateCcw className="h-3.5 w-3.5" /> Restaurar por defecto
        </Button>
      </CardHeader>

      <p className="-mt-2 mb-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
        Activa o desactiva las reglas y ajusta sus umbrales. Solo se aplican a tu organizacion.
      </p>

      {/* Pestañas por grupo */}
      <div className="mb-4 flex gap-1 overflow-x-auto border-b" style={{ borderColor: 'var(--color-border)' }}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className="relative flex shrink-0 items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors"
              style={{
                color: isActive ? 'var(--color-brand-secondary)' : 'var(--color-text-secondary)',
                borderBottom: isActive ? '2px solid var(--color-brand-secondary)' : '2px solid transparent',
                marginBottom: '-1px',
              }}
            >
              {tab.label}
              {tab.badge && (
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums"
                  style={{
                    backgroundColor: isActive ? 'var(--color-brand-secondary)' : 'var(--color-gray-200)',
                    color: isActive ? 'white' : 'var(--color-text-tertiary)',
                  }}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Tab: Limpieza de licencias (reglas seguras, ON por defecto) */}
      {activeTab === 'cleanup' && (
        <div className="space-y-4">
          <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            Reglas seguras de saneamiento. Activas por defecto: solo identifican licencias desperdiciadas, sin cambiar el plan del usuario.
          </p>
          <RuleCard
            icon={UserX}
            iconBg="var(--color-warning-light)"
            iconColor="var(--color-warning-dark)"
            title="Usuarios inactivos"
            description="Detecta licencias asignadas a usuarios que no han iniciado sesion en un periodo determinado"
            enabled={!!form.rule_inactive_enabled}
            onToggle={(v) => update('rule_inactive_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField
                label="Dias de inactividad"
                value={form.inactive_days_threshold}
                onChange={(v) => update('inactive_days_threshold', v)}
                suffix="dias"
                min={7}
                help="Se genera recomendacion si el usuario no inicia sesion en este periodo"
              />
              <NumberField
                label="Dias para severidad alta"
                value={form.inactive_high_severity_days}
                onChange={(v) => update('inactive_high_severity_days', v)}
                suffix="dias"
                min={30}
                help="Superado este umbral la severidad pasa de media a alta"
              />
            </div>
          </RuleCard>

          <RuleCard
            icon={UserMinus}
            iconBg="var(--color-error-light)"
            iconColor="var(--color-error-dark)"
            title="Cuentas deshabilitadas"
            description="Detecta licencias asignadas a cuentas deshabilitadas en Azure AD. Severidad siempre critica."
            enabled={!!form.rule_disabled_enabled}
            onToggle={(v) => update('rule_disabled_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField
                label="Dias deshabilitada antes de avisar"
                value={form.disabled_days_threshold}
                onChange={(v) => update('disabled_days_threshold', v)}
                suffix="dias"
                min={0}
                help="0 = avisa inmediatamente. Si se indica un valor, solo genera recomendacion si la cuenta lleva deshabilitada al menos estos dias."
              />
            </div>
          </RuleCard>

          <RuleCard
            icon={Package}
            iconBg="var(--color-success-light)"
            iconColor="var(--color-success-dark)"
            title="Licencias sobrantes"
            description="Detecta licencias compradas en exceso que no estan asignadas a ningun usuario"
            enabled={!!form.rule_excess_enabled}
            onToggle={(v) => update('rule_excess_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField
                label="Umbral % no asignadas"
                value={form.excess_pct_threshold}
                onChange={(v) => update('excess_pct_threshold', v)}
                suffix="%"
                min={5}
                max={90}
                help="Se detecta si el % de licencias sin asignar supera este umbral"
              />
              <NumberField
                label="Minimo unidades libres"
                value={form.excess_min_units}
                onChange={(v) => update('excess_min_units', v)}
                suffix="unidades"
                min={1}
                help="Solo se genera si hay al menos esta cantidad de unidades sin asignar"
              />
              <NumberField
                label="% reduccion conservadora"
                value={form.excess_reducible_pct}
                onChange={(v) => update('excess_reducible_pct', v)}
                suffix="%"
                min={10}
                max={100}
                help="Porcentaje de las licencias sobrantes que se propone reducir"
              />
            </div>
          </RuleCard>

          <RuleCard
            icon={Copy}
            iconBg="var(--color-brand-accent-2-light, var(--color-gray-100))"
            iconColor="var(--color-brand-accent-2, var(--color-text-secondary))"
            title="Licencias redundantes"
            description="Detecta usuarios con licencias duplicadas donde una ya incluye las funciones de la otra"
            enabled={!!form.rule_redundant_enabled}
            onToggle={(v) => update('rule_redundant_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                Pares de licencias redundantes detectados automaticamente: E5 + E3, E5 + Power BI Pro, E5 + EMS E5, E3 + EMS E3, E3 + E1, E5 + Business Essentials.
              </p>
            </div>
          </RuleCard>
        </div>
      )}

      {/* Tab: Optimización avanzada (alertas por infrautilización de servicio, OFF por defecto) */}
      {activeTab === 'advanced' && (
        <div className="space-y-4">
          <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            Marcan a usuarios activos cuyas métricas del servicio (últimos 30 días) están todas por debajo del umbral, o con licencia Copilot sin uso. Generan una alerta para revisión, sin cambiar licencias. Desactivadas por defecto.
          </p>

          <RuleCard
            icon={Mail}
            iconBg="var(--color-brand-primary-50)"
            iconColor="var(--color-brand-primary)"
            title="Exchange"
            description="Usuarios con apenas actividad de correo (enviados, recibidos y leídos por debajo del umbral)"
            enabled={!!form.rule_exchange_lowuse_enabled}
            onToggle={(v) => update('rule_exchange_lowuse_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField label="Min. enviados" value={form.exchange_min_sent} onChange={(v) => update('exchange_min_sent', v)} suffix="/mes" min={0} help="Alerta si envió menos de este número" />
              <NumberField label="Min. recibidos" value={form.exchange_min_received} onChange={(v) => update('exchange_min_received', v)} suffix="/mes" min={0} help="Alerta si recibió menos de este número" />
              <NumberField label="Min. leídos" value={form.exchange_min_read} onChange={(v) => update('exchange_min_read', v)} suffix="/mes" min={0} help="Alerta si leyó menos de este número" />
            </div>
          </RuleCard>

          <RuleCard
            icon={Cloud}
            iconBg="var(--color-brand-primary-50)"
            iconColor="var(--color-brand-primary)"
            title="OneDrive"
            description="Usuarios con apenas actividad en OneDrive (vistos, sincronizados y compartidos por debajo del umbral)"
            enabled={!!form.rule_onedrive_lowuse_enabled}
            onToggle={(v) => update('rule_onedrive_lowuse_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField label="Min. vistos" value={form.onedrive_min_viewed} onChange={(v) => update('onedrive_min_viewed', v)} suffix="archivos" min={0} help="Alerta si vio/editó menos de este número" />
              <NumberField label="Min. sincronizados" value={form.onedrive_min_synced} onChange={(v) => update('onedrive_min_synced', v)} suffix="archivos" min={0} help="Alerta si sincronizó menos de este número" />
              <NumberField label="Min. compartidos" value={form.onedrive_min_shared} onChange={(v) => update('onedrive_min_shared', v)} suffix="archivos" min={0} help="Alerta si compartió menos de este número" />
            </div>
          </RuleCard>

          <RuleCard
            icon={FileText}
            iconBg="var(--color-brand-primary-50)"
            iconColor="var(--color-brand-primary)"
            title="SharePoint"
            description="Usuarios con apenas actividad en SharePoint (vistos, sincronizados y páginas visitadas por debajo del umbral)"
            enabled={!!form.rule_sharepoint_lowuse_enabled}
            onToggle={(v) => update('rule_sharepoint_lowuse_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField label="Min. vistos" value={form.sharepoint_min_viewed} onChange={(v) => update('sharepoint_min_viewed', v)} suffix="archivos" min={0} help="Alerta si vio/editó menos de este número" />
              <NumberField label="Min. sincronizados" value={form.sharepoint_min_synced} onChange={(v) => update('sharepoint_min_synced', v)} suffix="archivos" min={0} help="Alerta si sincronizó menos de este número" />
              <NumberField label="Min. páginas visitadas" value={form.sharepoint_min_pages} onChange={(v) => update('sharepoint_min_pages', v)} suffix="páginas" min={0} help="Alerta si visitó menos páginas que este número" />
            </div>
          </RuleCard>

          <RuleCard
            icon={MessageSquare}
            iconBg="var(--color-brand-primary-50)"
            iconColor="var(--color-brand-primary)"
            title="Teams"
            description="Usuarios con apenas actividad en Teams (mensajes chat, llamadas y reuniones por debajo del umbral)"
            enabled={!!form.rule_teams_lowuse_enabled}
            onToggle={(v) => update('rule_teams_lowuse_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField label="Min. mensajes chat" value={form.teams_min_chat} onChange={(v) => update('teams_min_chat', v)} suffix="/mes" min={0} help="Alerta si envió menos mensajes de chat" />
              <NumberField label="Min. llamadas" value={form.teams_min_calls} onChange={(v) => update('teams_min_calls', v)} suffix="/mes" min={0} help="Alerta si hizo menos llamadas" />
              <NumberField label="Min. reuniones" value={form.teams_min_meetings} onChange={(v) => update('teams_min_meetings', v)} suffix="/mes" min={0} help="Alerta si participó en menos reuniones" />
            </div>
          </RuleCard>

          <RuleCard
            icon={Sparkles}
            iconBg="var(--color-brand-primary-50)"
            iconColor="var(--color-brand-primary)"
            title="Copilot sin uso"
            description="Usuarios con licencia Microsoft 365 Copilot (o Copilot for Business) sin actividad de Copilot en el periodo indicado, o que nunca lo han usado"
            enabled={!!form.rule_copilot_nouse_enabled}
            onToggle={(v) => update('rule_copilot_nouse_enabled', v ? 1 : 0)}
          >
            <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
              <NumberField
                label="Días sin actividad"
                value={form.copilot_inactive_days}
                onChange={(v) => update('copilot_inactive_days', v)}
                suffix="días"
                min={30}
                help="Se genera recomendación si la última actividad de Copilot es anterior a este periodo (90 = 3 meses). Sin actividad registrada nunca, la severidad es alta e incluye el ahorro estimado de la licencia. Los usuarios que ya detecta la regla de usuarios inactivos no se duplican aquí."
              />
            </div>
          </RuleCard>
        </div>
      )}

      {/* Tab: Excepciones y ciclo de vida */}
      {activeTab === 'exceptions' && (
        <div className="space-y-4">
          <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
            Excluye licencias o usuarios concretos y controla cuanto viven las recomendaciones.
          </p>
        {/* Exceptions: by license type and by users filter */}
        <ExceptionsSection />

        {/* Lifecycle settings */}
        <div className="rounded-lg border p-4" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--color-gray-100)' }}>
              <RotateCcw className="h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Ciclo de vida
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Configuracion de tiempos para la gestion automatica de recomendaciones
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
            <NumberField
              label="Cooldown al descartar"
              value={form.dismiss_cooldown_days}
              onChange={(v) => update('dismiss_cooldown_days', v)}
              suffix="dias"
              min={1}
              help="Tras descartar una recomendacion, no se vuelve a generar durante este periodo"
            />
            <NumberField
              label="Expiracion automatica"
              value={form.expiration_days}
              onChange={(v) => update('expiration_days', v)}
              suffix="dias"
              min={7}
              help="Las recomendaciones pendientes expiran automaticamente tras este periodo"
            />
          </div>
        </div>
        </div>
      )}

      {/* Sticky save bar */}
      {dirty && (
        <div className="sticky bottom-4 mt-4 flex items-center justify-between rounded-lg border px-4 py-3 shadow-lg"
          style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-brand-secondary)' }}>
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Hay cambios sin guardar
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => {
              if (config) {
                const { tenant_id: _, ...rest } = config as RulesConfig & Record<string, unknown>
                setForm({ ...DEFAULTS, ...rest })
              } else {
                setForm(DEFAULTS)
              }
              setDirty(false)
            }}>
              Descartar
            </Button>
            <Button size="sm" onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
              <Save className="h-3.5 w-3.5" /> Guardar
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
